import { useRef, useState } from 'react';
import {
  Typography, Box, Stack, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, IconButton, CircularProgress,
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';

import {
  ITicketHistorial,
  ITicketArchivo,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import {
  descargarArchivo,
  obtenerUrlArchivo,
  useFetchTicketArchivos,
} from '@/shared/actions/erp/tickets-soporte-cliente';

type TimelineHistorialProps = {
  historial: ITicketHistorial[];
  ticketId?: string;
};

type DotColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'grey';

const getColorByAccion = (accion: string): DotColor => {
  const lower = accion.toLowerCase();
  if (lower.includes('crear') || lower.includes('creacion')) return 'success';
  if (lower.includes('escalar') || lower.includes('escalamiento')) return 'warning';
  if (lower.includes('asignar') || lower.includes('asignacion')) return 'info';
  if (lower.includes('cerrar') || lower.includes('cierre')) return 'error';
  if (lower.includes('comentario') || lower.includes('archivo')) return 'secondary';
  return 'primary';
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const iconForContentType = (ct?: string | null) => {
  if (!ct) return <DescriptionIcon fontSize="small" />;
  if (ct.startsWith('image/')) return <ImageIcon fontSize="small" />;
  if (ct.includes('pdf')) return <PictureAsPdfIcon fontSize="small" />;
  if (ct.includes('zip')) return <FolderZipIcon fontSize="small" />;
  return <DescriptionIcon fontSize="small" />;
};

/**
 * Modal de previsualizacion de imagenes con zoom (+/-, rueda) y paneo
 * (arrastrar). Botones: zoom-, zoom+, restablecer, descargar y cerrar.
 * Solo se usa para archivos tipo imagen.
 */
function ImagePreviewModal({
  archivo,
  url,
  onClose,
}: {
  archivo: ITicketArchivo;
  url: string;
  onClose: () => void;
}) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const [arrastrando, setArrastrando] = useState(false);

  const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
  const zoomIn = () => setScale((s) => clamp(s * 1.25, 0.5, 8));
  const zoomOut = () => setScale((s) => clamp(s / 1.25, 0.5, 8));
  const reset = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setScale((s) => clamp(s * (e.deltaY < 0 ? 1.12 : 0.89), 0.5, 8));
  };
  const onMouseDown = (e: React.MouseEvent) => {
    drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    setArrastrando(true);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!drag.current) return;
    setOffset({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const endDrag = () => { drag.current = null; setArrastrando(false); };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
          {archivo.nombre_original}
        </Typography>
        <Tooltip title="Alejar">
          <IconButton size="small" onClick={zoomOut}><ZoomOutIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ minWidth: 42, textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </Typography>
        <Tooltip title="Acercar">
          <IconButton size="small" onClick={zoomIn}><ZoomInIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Restablecer">
          <IconButton size="small" onClick={reset}><RestartAltIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Descargar">
          <IconButton size="small" onClick={() => descargarArchivo(archivo.id_ticket_archivo)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cerrar">
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: 'grey.900',
          height: '72vh',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: arrastrando ? 'grabbing' : 'grab',
        }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        <img
          src={url}
          alt={archivo.nombre_original}
          draggable={false}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transition: arrastrando ? 'none' : 'transform 0.08s ease-out',
            maxWidth: '100%',
            maxHeight: '100%',
            userSelect: 'none',
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Chip clickeable de un adjunto.
 *   - IMAGEN: al click previsualiza en un modal (zoom/paneo) sin descargar;
 *     desde el modal se puede descargar o cerrar.
 *   - OTRO archivo: al click descarga (pide al backend la URL firmada de MinIO
 *     y la abre; no se usa <a href> porque el browser no manda Authorization).
 */
function ArchivoChip({ archivo }: { archivo: ITicketArchivo }) {
  const esImagen = !!archivo.content_type?.startsWith('image/');
  const [preview, setPreview] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  const abrirPreview = async () => {
    if (cargando) return;
    setCargando(true);
    const url = await obtenerUrlArchivo(archivo.id_ticket_archivo);
    setCargando(false);
    if (url) setPreview(url);
  };

  return (
    <>
      <Tooltip title={`${archivo.nombre_original} (${formatBytes(archivo.tamano_bytes)})`}>
        <Chip
          onClick={esImagen ? abrirPreview : () => descargarArchivo(archivo.id_ticket_archivo)}
          clickable
          icon={iconForContentType(archivo.content_type)}
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" noWrap sx={{ maxWidth: 180 }}>
                {archivo.nombre_original}
              </Typography>
              {cargando ? (
                <CircularProgress size={12} thickness={6} />
              ) : esImagen ? (
                <VisibilityIcon fontSize="inherit" />
              ) : (
                <DownloadIcon fontSize="inherit" />
              )}
            </Stack>
          }
          size="small"
          variant="outlined"
          color={esImagen ? 'primary' : 'default'}
        />
      </Tooltip>
      {preview && (
        <ImagePreviewModal archivo={archivo} url={preview} onClose={() => setPreview(null)} />
      )}
    </>
  );
}

export default function TimelineHistorial({ historial, ticketId }: TimelineHistorialProps) {
  // Carga la lista flat de archivos del ticket y los agrupa por historial_id
  // para evitar N+1 fetches (un solo request por ticket).
  const { data: archivosResp } = useFetchTicketArchivos(ticketId ?? '');
  const archivosFlat = (archivosResp?.data ?? []) as ITicketArchivo[];
  const archivosPorHistorial = new Map<string, ITicketArchivo[]>();
  for (const a of archivosFlat) {
    if (!a.historial_id) continue;
    const arr = archivosPorHistorial.get(a.historial_id) ?? [];
    arr.push(a);
    archivosPorHistorial.set(a.historial_id, arr);
  }

  if (!historial || historial.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No hay historial disponible.
        </Typography>
      </Box>
    );
  }

  return (
    <Timeline position="alternate">
      {historial.map((entry, index) => {
        const archivos = archivosPorHistorial.get(entry.id_ticket_historial) ?? [];
        return (
          <TimelineItem key={entry.id_ticket_historial}>
            <TimelineSeparator>
              <TimelineDot color={getColorByAccion(entry.accion)} />
              {index < historial.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Typography variant="subtitle2" fontWeight="bold">
                {entry.accion}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {entry.detalle}
              </Typography>
              {archivos.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {archivos.map(a => (
                    <ArchivoChip key={a.id_ticket_archivo} archivo={a} />
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary">
                {entry.usuario_nombre} &mdash;{' '}
                {dayjs(entry.fecha).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}
